import { useMemo } from 'react';

import { TicketGroupingKind } from '@neo.tax/shared';

type GroupingTicketOrCollection = {
  id: string;
  identifier: string;
  name: string;
  kind: TicketGroupingKind;
  count: number;
};

export type GroupingSelection = {
  isGroupingSelected: boolean;
  selectedTicketIds: Set<string>;
};

export interface UseTicketSelectionsParams {
  data: GroupingTicketOrCollection[] | undefined;
  groupingSelections: Record<string, GroupingSelection>;
}

export interface UseTicketSelectionsReturn {
  groupingCollectionIds: string[];
  groupingTicketIds: string[];
  includeNoParentTickets: boolean;
  individualTicketIds: Set<string>;
  selectedItemsSummary: {
    text: string;
    hasSelections: boolean;
  };
}

export const useTicketSelections = ({
  data,
  groupingSelections,
}: UseTicketSelectionsParams): UseTicketSelectionsReturn =>
  useMemo(() => {
    if (!data)
      return {
        groupingTicketIds: [],
        groupingCollectionIds: [],
        includeNoParentTickets: false,
        individualTicketIds: new Set<string>(),
        selectedItemsSummary: { text: '', hasSelections: false },
      };

    const groupingTickets: string[] = [];
    const groupingCollections: string[] = [];
    let hasNoParentTickets = false;
    const individualTickets = new Set<string>();

    let groupCount = 0;
    let collectionCount = 0;
    let individualTicketCount = 0;

    data.forEach((grouping) => {
      const selection = groupingSelections[grouping.id];
      if (selection?.isGroupingSelected) {
        // Whole grouping is selected
        if (grouping.kind === TicketGroupingKind.GroupingTicket) {
          groupingTickets.push(grouping.id);
          groupCount += 1;
        } else if (grouping.kind === TicketGroupingKind.GroupingCollection) {
          groupingCollections.push(grouping.id);
          collectionCount += 1;
        } else if (grouping.kind === TicketGroupingKind.NoParent) {
          hasNoParentTickets = true;
          // NoParent represents individual tickets without a parent grouping
          individualTicketCount += grouping.count;
        }
      } else if (selection?.selectedTicketIds.size > 0) {
        // Individual tickets are selected
        selection.selectedTicketIds.forEach((ticketId) => individualTickets.add(ticketId));
        individualTicketCount += selection.selectedTicketIds.size;
      }
    });

    // Generate summary text
    const parts: string[] = [];
    if (groupCount > 0) {
      parts.push(`${groupCount} Group${groupCount > 1 ? 's' : ''}`);
    }
    if (collectionCount > 0) {
      parts.push(`${collectionCount} Project${collectionCount > 1 ? 's' : ''}`);
    }
    if (individualTicketCount > 0) {
      parts.push(`${individualTicketCount} ticket${individualTicketCount > 1 ? 's' : ''}`);
    }

    const hasSelections = parts.length > 0;
    let text = '';

    if (hasSelections) {
      if (parts.length === 1) {
        text = `${parts[0]} selected`;
      } else if (parts.length === 2) {
        text = `${parts[0]} and ${parts[1]} are selected`;
      } else {
        const lastPart = parts.pop();
        text = `${parts.join(', ')}, and ${lastPart} are selected`;
      }
    }

    return {
      groupingTicketIds: groupingTickets,
      groupingCollectionIds: groupingCollections,
      includeNoParentTickets: hasNoParentTickets,
      individualTicketIds: individualTickets,
      selectedItemsSummary: { text, hasSelections },
    };
  }, [data, groupingSelections]);
